/**
 * DriverPayAdditionModal — port of V1 lib_editrecord.pl::driverpayaddition.
 *
 * Three form variants matching V1's branching:
 *   1. Drayage group  — ADDITIONAL DRAYAGE | BOBTAIL | ADDITIONAL HAZMAT | ADDITIONAL DAYCAB
 *      Fields: Driver, Date, Origin, Destination, Rate
 *   2. Waiting Time   — WAITING TIME | MEMPHIS RAIL WAITING TIME
 *      Fields: Driver, Date, Time In, Time Out, Hours, Per Rate, Amount, Location
 *   3. Default        — everything else (SCALE, LAYOVER, BONUS, …)
 *      Fields: Driver, Date, Origin, [Destination if existing], Rate
 *
 * Shared bottom: Description (textarea), Pay Status + Reason (DO NOT PAY /
 * DEFER only), read-only Approved By + Approved At + Payroll Date. Delete is
 * a footer button on existing rows (no inline Deleted toggle).
 * Fuel Type + the GWI commission preview (Comm / Net) sit next to the Rate
 * row per Bug 32837.
 *
 * Phase 1 scope: no rate auto-lookup, no email notifications, no doLineItemPro
 * for new WAITING TIME / CLEANING rows. Those are V1 parity gaps tracked in
 * the backend driver_pay_additions.py docstring.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import DateTimePicker from '../DateTimePicker';
import TypeAhead, { type TypeAheadOption } from '../TypeAhead';
import PermissionsContainer, { useResolvedAccessLevel } from '../PermissionsContainer';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

/**
 * Component-permission identifiers for driver-pay-addition write actions
 * (Add / Save / Delete). PermissionsContainer keys off these so admins
 * can configure access via the Permissions Mode cog overlay; when access
 * resolves to 0 the wrapped button hides at runtime.
 */
export const DRIVER_PAY_PERMISSION_ID = 'pro-addl-cost-driver-pay-write';
export const DRIVER_PAY_PERMISSION_NAME = 'Pro Addl Cost — Driver Pay Add/Edit';
import { parseMdYDate } from '../../utils/dateUtils';
import { gwiCommissionFor, formatMoney } from '../../utils/gwiCommission';
import { useToast } from '../../contexts/ToastContext';
import {
  driverPayAdditionsService,
  type DriverPayAddition,
  type DriverPayAdditionPatch,
  type DriverPayAdditionReferenceData,
  type DriverPayAdditionTypeOption,
} from '../../services/driverPayAdditions.service';
import LocationTypeAhead from '../LocationTypeAhead';
import { searchLocations } from '../../services/locations.service';
import type { LocationLite } from '../../types/locations';

/** Format a Date as mm/dd/yyyy (the shape the V1 form + the backend
 *  expect for eventDate). Returns '' for null. */
const formatMdYDate = (d: Date | null): string => {
  if (!d) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
};

/** Format a Date as HH:MM for the WAITING TIME variant's time fields. */
const formatHHMM = (d: Date | null): string => {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export interface InvoiceLineItemToast {
  message: string;
  type: 'success' | 'warning' | 'info';
}

/**
 * Map the backend's Bobtail / Waiting Time invoice line-item auto-add outcome
 * (create-response only) to the toast the modal shows. Returns null when there
 * is nothing to announce — no outcome (a non-invoice type, or an update).
 * Pure so the message/severity matrix is unit-testable without the modal.
 */
export function invoiceLineItemToast(
  outcome: DriverPayAddition['invoiceLineItemOutcome'],
  label: string | null | undefined,
): InvoiceLineItemToast | null {
  if (!outcome) return null;
  // Noun phrase for the charge. `label` is effectively always present (the
  // backend sets it alongside the outcome); the fallbacks keep the sentence
  // grammatical if it ever isn't — `lead` for sentence-start, `mid` inline.
  const lead = label ? `${label} charge` : 'Charge';
  const mid = label ? `${label} charge` : 'charge';
  switch (outcome) {
    case 'added':
      return { message: `${lead} added to the invoice.`, type: 'success' };
    case 'trouble_ticket':
      return {
        message: `Invoice is closed — a trouble ticket was created to add the ${mid}.`,
        type: 'warning',
      };
    case 'no_invoice':
      return {
        message: `Couldn't auto-add the ${mid} — this order has no open invoice.`,
        type: 'info',
      };
    case 'cap':
      return {
        message: `Couldn't auto-add the ${mid} — the invoice already has the maximum line items.`,
        type: 'info',
      };
    default:
      return null;
  }
}

/** Parse 'HH:MM' to a Date so DateTimePicker can render it. */
const parseHHMM = (s: string | undefined): Date | null => {
  if (!s || !/^\d{1,2}:\d{2}$/.test(s.trim())) return null;
  const [h, m] = s.trim().split(':').map((p) => parseInt(p, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pronumber: number;
  /** Recno of the row being edited. Null = new addition. */
  recno: number | null;
  /** When opening for a new addition, optionally pre-select a type. */
  defaultType?: string;
  /** Called after a successful create / update / delete; the host should
   *  re-fetch order details so the AddlCost table reflects the change.
   *  May return a Promise; the modal awaits it before closing so the
   *  table is up-to-date the moment the modal disappears. */
  onSaved: () => void | Promise<void>;
  /** Existing additions for this PRO — used to warn on duplicate type+rate
   *  (V1 lib_editrecord.pl:3646 checkduperate). */
  existingAdditions?: Array<{ type: string; rate: number; recno?: number }>;
}

type FormState = Partial<DriverPayAddition>;

/**
 * Duplicate rate check (V1 lib_editrecord.pl:3463, 3986). V1's list also
 * included BONUS and ROADABILITY ADDITIONAL PAY, but those types are no
 * longer offered in production — only the four below are active.
 */
const DUPLICATE_CHECK_TYPES = new Set([
  'ADDITIONAL DRAYAGE',
  'ADDITIONAL SERVICES',
  'ADDITIONAL HAZMAT',
  'BOBTAIL',
]);

/**
 * Soft pre-save confirmations — the messages V1 raised as sequential
 * window.confirm() gates (lib_editrecord.pl:3563-3604, 3646, and the >$5000
 * check). Returns the human-readable warning lines that apply to the pending
 * save; an empty array means the save can proceed with no confirmation modal.
 * `now` (ms since epoch) is injected so the ±30-day window is deterministic in
 * tests. Pure so the message matrix is unit-testable without the modal.
 */
export function computeSaveWarnings(
  form: Pick<FormState, 'type' | 'rate' | 'eventDate'>,
  existingAdditions: Array<{ type: string; rate: number; recno?: number }> | undefined,
  recno: number | null,
  now: number,
): string[] {
  const warnings: string[] = [];
  const eventD = parseMdYDate(form.eventDate ?? '');
  if (eventD) {
    const days = (eventD.getTime() - now) / (1000 * 60 * 60 * 24);
    if (days < -30) {
      warnings.push('Event date is more than 30 days in the past.');
    } else if (days > 30) {
      warnings.push('Event date is more than 30 days in the future.');
    }
  }
  // Duplicate type+rate — only for the drayage-ish types V1 gates on.
  if (existingAdditions && DUPLICATE_CHECK_TYPES.has(form.type ?? '')) {
    const isDuplicate = existingAdditions.some(
      (a) => a.recno !== (recno ?? undefined) && a.type === form.type && a.rate === (form.rate ?? 0),
    );
    if (isDuplicate) {
      warnings.push(`A ${form.type} addition with rate $${form.rate} already exists for this PRO.`);
    }
  }
  if ((form.rate ?? 0) > 5000) {
    warnings.push('Rate is above $5000.');
  }
  return warnings;
}

/**
 * Types the backend (driver_pay_additions.py create_addition) rejects without
 * a fuelType. Kept in lockstep so users see an asterisk instead of a 400.
 */
const FUEL_TYPE_REQUIRED_TYPES = new Set([
  'ADDITIONAL DRAYAGE',
  'CLEANING',
  'RESPOTTING',
  'SCALE',
]);

export interface RequiredFields {
  type: boolean;
  driver: boolean;
  eventDate: boolean;
  origin: boolean;
  destination: boolean;
  rate: boolean;
  timeIn: boolean;
  fuelType: boolean;
}

/**
 * Per-type required-field map. Drives both the label asterisks and the
 * pre-submit missing-fields banner, and mirrors the backend's create_addition
 * checks so the two stay in lockstep.
 *
 * Locations are required ONLY for BOBTAIL (origin + destination feed its
 * zone-based rate lookup) and WAITING TIME (its single "Location" = origin).
 * Every other type may be saved without a location. Pure so the matrix is
 * unit-testable without the modal.
 */
export function computeRequiredFields(type: string, variant: string): RequiredFields {
  const t = type ?? '';
  return {
    type: true,
    driver: !!t,
    eventDate: !!t,
    origin: t === 'BOBTAIL' || variant === 'waiting',
    destination: t === 'BOBTAIL',
    // BOBTAIL rate is zone-derived; WAITING TIME rate is hours×perrate.
    // Both render the Rate input as read-only, so don't asterisk them.
    rate: !!t && t !== 'BOBTAIL' && variant !== 'waiting',
    timeIn: variant === 'waiting',
    fuelType: FUEL_TYPE_REQUIRED_TYPES.has(t),
  };
}

/**
 * Types whose Pay field is auto-filled from driverpay_rates_other and
 * locked (V1 lib_editrecord.pl:3958-3965). Active types: LAYOVER, SCALE TICKET.
 * BOBTAIL is locked separately because its rate comes from driverpay_rates
 * by origin/destination zones (V1:3742-3744).
 */
const AUTO_RATE_LOCK_TYPES = new Set([
  'LAYOVER',
  'SCALE TICKET',
]);

/** Defaults for a new addition (or a reset after the user switches the
 *  Type dropdown). Keep in sync with what the backend expects on create. */
const freshForm = (type: string): FormState => ({
  type,
  driver: '',
  eventDate: '',
  rate: 0,
  description: '',
  deleted: 'N',
  gwiCommission: '',
  donotpayReason: '',
  fuelType: '',
  payStatus: '',
  intime: '',
  outtime: '',
  hours: 0,
  origCode: 0,
  destCode: 0,
  origName: '',
  destName: '',
});

/** Merge the RBAC-gated manager-only types into reference-data's base list,
 *  deduped by name and sorted alphabetically (matching V1's picker order).
 *  Base types win on a name clash. A non-manager gets an empty managerTypes
 *  list, so the returned reference data is unchanged. */
const mergeManagerTypes = (
  ref: DriverPayAdditionReferenceData,
  managerTypes: DriverPayAdditionTypeOption[],
): DriverPayAdditionReferenceData => {
  if (!managerTypes.length) return ref;
  const seen = new Set(ref.additionTypes.map((t) => t.name));
  const extra = managerTypes.filter((t) => !seen.has(t.name));
  if (!extra.length) return ref;
  const additionTypes = [...ref.additionTypes, ...extra].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  return { ...ref, additionTypes };
};

const Label: React.FC<{ children: React.ReactNode; htmlFor?: string; required?: boolean }> = ({
  children,
  htmlFor,
  required = false,
}) => (
  <label htmlFor={htmlFor} className="block text-[0.625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-0.5">
    {children}
    {required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
  </label>
);

const inputClass =
  'w-full h-7 px-1.5 text-[0.75rem] rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100 ' +
  'focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 disabled:bg-slate-100 dark:disabled:bg-gray-700 disabled:text-slate-500 dark:disabled:text-gray-400';

const readonlyClass =
  'w-full h-7 px-1.5 text-[0.75rem] rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300 cursor-not-allowed';

/** Small read-only money display used for the GWI commission preview
 *  (Comm / Net) next to the Rate. Hoisted to module scope — it only takes
 *  props, so there is no reason to redefine it each render (Bug 32837). Takes
 *  a pre-formatted string (via the shared formatMoney) so the sign/rounding
 *  live at the call site and can't drift from the AddlCostView table. */
const ReadonlyMoney: React.FC<{ label: string; value: string; negative?: boolean }> = ({
  label,
  value,
  negative,
}) => (
  <div>
    <Label>{label}</Label>
    <input
      className={`${readonlyClass} text-right ${negative ? 'text-red-600 dark:text-red-300' : ''}`}
      value={value}
      readOnly
      aria-label={label}
    />
  </div>
);

const DriverPayAdditionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  pronumber,
  recno,
  defaultType,
  onSaved,
  existingAdditions,
}) => {
  const { showToast } = useToast();
  const [refData, setRefData] = useState<DriverPayAdditionReferenceData | null>(null);
  const [form, setForm] = useState<FormState>({});
  const [perRate, setPerRate] = useState<number>(0);
  const [fuelPreview, setFuelPreview] = useState<{
    key: string; percent: number | null;
  } | null>(null);
  // True while an auto-rate lookup is in flight (BOBTAIL / waiting / scale etc.),
  // so read-only rate fields can show a "Pulling Rate…" indicator.
  const [rateLoading, setRateLoading] = useState(false);
  // #33649 — the driverpay_rates row the engine matched for this BOBTAIL lane.
  // 0 = MISSING RATE (no zone rate on file); null = not looked up yet, so the
  // warning doesn't flash before the first lookup lands. Seeded from the stored
  // row when editing, because a locked (paid) row never runs a fresh lookup.
  const [rateRecno, setRateRecno] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Over-limit confirm overlay: set when Save is clicked with a rate over the
  // type's limit. The user confirms (with an optional reason) to proceed; the
  // override is logged to the Pro History tab by the backend.
  const [limitConfirm, setLimitConfirm] = useState<{ rate: number; limit: number } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  // Soft pre-save confirmation overlay (was V1's sequential window.confirm
  // gates: ±30-day date, duplicate type+rate, rate over $5000). Holds the
  // applicable warning lines; the user confirms once to proceed to save.
  const [saveWarnings, setSaveWarnings] = useState<string[] | null>(null);
  // Delete confirmation overlay (was window.confirm on the footer Delete).
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  /** Tracks whether the user manually edited the rate; if true we won't
   *  overwrite it during auto-lookups. Reset whenever the modal re-opens or
   *  the type changes. */
  const rateTouchedRef = useRef(false);

  // Load reference data + (if editing) the existing row each time we open.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    setLimitConfirm(null);
    setOverrideReason('');
    setSaveWarnings(null);
    setDeleteConfirm(false);
    setLoading(true);
    (async () => {
      try {
        const refPromise = driverPayAdditionsService.fetchReferenceData(pronumber);
        const rowPromise = recno != null
          ? driverPayAdditionsService.fetchAddition(pronumber, recno)
          : Promise.resolve(null);
        // Manager-only types (e.g. BONUS) come from a separate RBAC-gated
        // endpoint; resolves to [] for non-managers. Merge them into the Type
        // dropdown so only permitted users can pick them.
        const mgrPromise = driverPayAdditionsService.fetchManagerAdditionTypes(pronumber);
        const [ref, row, managerTypes] = await Promise.all([refPromise, rowPromise, mgrPromise]);
        if (cancelled) return;
        setRefData(mergeManagerTypes(ref, managerTypes));
        if (row) {
          setForm({ ...row });
          // #33649 — seed from the row. A locked (paid) row skips the live
          // lookup entirely, so this is its only source; for an unlocked row the
          // lookup below overwrites it a moment later.
          // #33741 — effectiveRateRecno, not the raw stored column: a paid row
          // created before #33649 has a stale 0 there and no way to heal it.
          setRateRecno(row.effectiveRateRecno ?? row.rateRecno ?? null);
          // WAITING TIME stores only hours + amount (hours × perrate), and a
          // locked (paid) row never runs the live lookup, so derive the
          // per-hour rate from the row. An unlocked row's lookup overwrites it.
          const hrs = Number(row.hours ?? 0);
          const amt = Number(row.rate ?? 0);
          setPerRate(hrs > 0 && amt > 0 ? Math.round((amt / hrs) * 100) / 100 : 0);
        } else {
          setForm(freshForm(defaultType ?? ''));
          setRateRecno(null);
          setPerRate(0);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const detail =
            typeof err === 'object' && err && 'response' in err
              ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
              : undefined;
          setError(detail || (err instanceof Error ? err.message : 'Failed to load addition'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pronumber, recno, defaultType]);

  // Resolve the selected type's metadata from refData. While refData is
  // still loading or the user has not picked a type yet, fall back to
  // 'default' so the form renders the safest variant.
  const selectedType = useMemo(
    () => refData?.additionTypes.find((t) => t.name === form.type),
    [refData, form.type],
  );
  const variant = selectedType?.variant ?? 'default';

  // Once the addition's payroll has been invoiced the backend rejects any
  // edit/delete (driver_pay_additions._check_paid_lock, 409). Reflect that in
  // the UI: the whole form goes view-only and Save/Delete are removed.
  const locked = !!form.isPaid;

  // Bug 32837 — GWI commission preview. When the selected type earns
  // commission we show read-only Comm (−30%) and Net (70% plus fuel) next to
  // the Rate, live as the user types. Legacy/unknown types fall back to the stored
  // gwiCommission char so an existing 'Y' row still previews correctly.
  // Uses the shared gwiCommissionFor/formatMoney so the modal and the
  // AddlCostView table render byte-identical values.
  const commissionApplies = selectedType
    ? selectedType.gwiCommission
    : (form.gwiCommission ?? '') === 'Y';
  const rateNum = form.rate ?? 0;
  const fuelPreviewKey = JSON.stringify([pronumber, form.eventDate, form.fuelType]);
  const needsFuelPreview = !!form.fuelType && !!form.eventDate;
  const fuelPercent = needsFuelPreview
    ? (fuelPreview?.key === fuelPreviewKey ? fuelPreview.percent : null)
    : 0;
  const fuelAmount = fuelPercent == null ? null : rateNum * fuelPercent / 100;
  const { commission, net } = gwiCommissionFor(rateNum, commissionApplies ? 'Y' : 'N', fuelAmount ?? 0);
  const netDisplay = fuelAmount == null ? '—' : formatMoney(net ?? rateNum);

  // Fuel is a read-only preview, including on locked rows. Keep it separate
  // from auto-rating so looking up fuel never changes the stored base rate.
  useEffect(() => {
    if (!isOpen || loading || !needsFuelPreview) return;
    let cancelled = false;
    setFuelPreview(null);
    driverPayAdditionsService.lookupFuelPercent(pronumber, {
      eventDate: form.eventDate ?? '', fuelType: form.fuelType ?? '',
    }).then((percent) => {
      if (!cancelled) setFuelPreview({ key: fuelPreviewKey, percent });
    }).catch(() => {
      if (!cancelled) setFuelPreview({ key: fuelPreviewKey, percent: null });
    });
    return () => { cancelled = true; };
  }, [isOpen, loading, needsFuelPreview, pronumber, form.eventDate, form.fuelType, fuelPreviewKey]);

  // Per-type max (0 = none). Block save when the entered rate exceeds it — but
  // only for rates the user can actually edit. Auto-rate types (BOBTAIL,
  // AUTO_RATE_LOCK_TYPES, waiting) render a read-only rate, so a positive limit
  // there would deadlock Save with no editable path; the backend
  // _enforce_limit still guards those (surfacing a 400 instead).
  const rateIsReadOnly = form.type === 'BOBTAIL'
    || AUTO_RATE_LOCK_TYPES.has(form.type ?? '')
    || variant === 'waiting';
  const limitAmount = selectedType?.limitAmount ?? 0;
  const limitExceeded = !rateIsReadOnly && limitAmount > 0 && rateNum > limitAmount;

  // Do-not-pay reason applies to DO NOT PAY and DEFER (V1 parity).
  const reasonApplies = ['DO NOT PAY', 'DEFER'].includes((form.payStatus ?? '').trim());

  // #33649 — MISSING RATE. A BOBTAIL is the only type here that resolves through
  // the zone rate engine, so it's the only one where "no rate row" is a defect
  // rather than the norm; every other type is hand-rated. `rateRecno === 0` is
  // the engine saying it found nothing for this lane on this date — which is
  // NOT the same as a rate of 0.00, and is exactly what V1 blocks approval on
  // (savedriverpayaddition:4262 "stop approval if lineitem amount is 0").
  // Shown for DO NOT PAY too: the rate really is missing either way. Only the
  // PAY block below is skipped there, mirroring the backend.
  const missingBobtailRate = form.type === 'BOBTAIL' && rateRecno === 0;
  const payBlockedByMissingRate =
    missingBobtailRate && (form.payStatus ?? '').trim().toUpperCase() !== 'DO NOT PAY';
  const missingRateMessage =
    'MISSING RATE — no Bobtail rate is on file for this lane. '
    + "Add the rate in Driver Pay Rates; until then this line can't be approved for PAY.";

  // Legacy support: a saved type/paystatus no longer offered still shows.
  const typeOptions = useMemo(() => {
    const names = refData?.additionTypes.map((t) => t.name) ?? [];
    const legacy = form.type && !names.includes(form.type) ? [form.type] : [];
    return { names, legacy };
  }, [refData, form.type]);
  const payStatusOptions = useMemo(() => {
    const base = refData?.payStatuses ?? [];
    const cur = form.payStatus ?? '';
    return cur && !base.includes(cur) ? [...base, cur] : base;
  }, [refData, form.payStatus]);

  // Per-type required-field map (see computeRequiredFields). Drives both the
  // label asterisks and the pre-submit missing-fields banner, kept in lockstep
  // with the backend's create_addition checks.
  const required = useMemo(
    () => computeRequiredFields(form.type ?? '', variant),
    [form.type, variant],
  );

  const onField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (key === 'rate') rateTouchedRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Bug 32837 — Fuel Type now sits next to the Rate row (drayage/default
  // variants). Build the field once here (optional-chaining refData so it is
  // safe before load) and drop it into each variant's rate row below.
  const showsFuel = !!form.fuelType || selectedType?.showsFuelType
    || form.type === 'CLEANING' || form.type === 'RESPOTTING' || form.type === 'SCALE';
  const fuelField = showsFuel ? (
    <div>
      <Label required={required.fuelType}>Fuel Type</Label>
      {form.type === 'BOBTAIL' ? (
        // V1 line 4011-4017: BOBTAIL fuel type comes from the rate engine.
        <input className={readonlyClass} value={form.fuelType ?? ''} readOnly
          aria-label="Fuel Type (auto-set by rate engine)" />
      ) : (
        <select className={inputClass} value={form.fuelType ?? ''}
          onChange={(e) => onField('fuelType', e.target.value)}>
          {(refData?.fuelTypes ?? []).map((f) => (<option key={f || '_e'} value={f}>{f || '—'}</option>))}
        </select>
      )}
    </div>
  ) : null;
  const fuelAmountField = showsFuel ? (
    <ReadonlyMoney label="Fuel Amount" value={fuelAmount == null ? '—' : formatMoney(fuelAmount)} />
  ) : null;

  // Reset the touched flag when the modal re-opens or the type switches; new
  // additions should let the rate auto-fill on the first lookup.
  useEffect(() => {
    rateTouchedRef.current = false;
  }, [isOpen, form.type]);

  // Auto-rate lookup whenever inputs change. The endpoint is cheap (a couple
  // of indexed point queries) and returns instantly. We only overwrite the
  // user's rate when the user hasn't manually touched it. WAITING TIME's
  // perrate goes into local state and drives calctime below.
  useEffect(() => {
    // Don't auto-pull a rate for a locked (paid) row — it's view-only and a
    // fresh lookup would misrepresent the stored, already-paid amount.
    if (!isOpen || !form.type || locked) return;
    let cancelled = false;
    setRateLoading(true);
    (async () => {
      try {
        const res = await driverPayAdditionsService.lookupRate(pronumber, {
          type: form.type ?? '',
          eventDate: form.eventDate ?? '',
          origCode: form.origCode ?? 0,
          destCode: form.destCode ?? 0,
        });
        if (cancelled) return;
        setRateRecno(res.raterecno ?? 0);
        if (res.perrate != null) {
          setPerRate(res.perrate);
        } else {
          setPerRate(0);
        }
        // BOBTAIL / SCALE / LAYOVER etc — only overwrite the form rate when
        // the user hasn't typed something themselves.
        if (!res.noAutoRate && res.perrate == null && !rateTouchedRef.current && res.rate >= 0) {
          setForm((prev) => ({ ...prev, rate: res.rate, fuelType: res.fuelType || prev.fuelType }));
        }
      } catch {
        // swallow — auto-rate is best-effort
      } finally {
        if (!cancelled) setRateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pronumber, form.type, form.eventDate, form.origCode, form.destCode, locked]);

  // P2.B — When the selected driver is an hourly company driver, V1 forces
  // paystatus = DO NOT PAY (lib_editrecord.pl:3622-3638). We only do this
  // on new additions and only if the user hasn't already chosen a paystatus.
  useEffect(() => {
    if (!refData || recno != null) return;
    const driverOpt = refData.drivers.find((d) => d.unitnumber === form.driver);
    if (driverOpt?.payrollHourly && !(form.payStatus ?? '').trim()) {
      setForm((prev) => ({ ...prev, payStatus: 'DO NOT PAY' }));
    }
  }, [refData, recno, form.driver, form.payStatus]);

  // WAITING TIME calctime (V1 lib_editrecord.pl:3779-3815): when both
  // intime and outtime are present, compute totalHours = (out-in), subtract
  // the 2-hour free window, and store hours + amount = hours * perrate.
  useEffect(() => {
    if (variant !== 'waiting' || locked) return;
    const tin = parseHHMM(form.intime ?? '');
    const tout = parseHHMM(form.outtime ?? '');
    if (!tin || !tout) return;
    let totalHours = (tout.getHours() - tin.getHours()) + (tout.getMinutes() - tin.getMinutes()) / 60;
    if (totalHours < 0) totalHours += 24; // wrap past midnight (V1 same)
    const wtFree = 2;
    const billable = totalHours > wtFree ? totalHours - wtFree : 0;
    const amount = perRate > 0 ? Math.round(billable * perRate * 100) / 100 : 0;
    setForm((prev) => ({ ...prev, hours: billable, rate: amount }));
  }, [variant, form.intime, form.outtime, perRate]);

  // View-level (1) callers get the whole modal read-only: the container only
  // hides at 0, so anything below Edit (2) must disable itself here.
  const saveAccess = useResolvedAccessLevel(DRIVER_PAY_PERMISSION_ID);
  const canWrite = saveAccess >= 2;

  const handleSave = async () => {
    if (!canWrite) return;
    // V1 lib_editrecord.pl saveclick chain (lines 3715-3744, 3866-3870,
    // 3941-3949). Required-field rules live in the `required` memo above
    // so the asterisks in the form stay in sync with what we enforce here.
    const hasOrig = (form.origCode ?? 0) > 0;
    const hasDest = (form.destCode ?? 0) > 0;
    // Any non-zero value counts as "entered" — negative rates are valid
    // (e.g. deductions / credits), so don't gate this on > 0.
    const hasRate = (form.rate ?? 0) !== 0;
    const hasFuelType = (form.fuelType ?? '').trim().length > 0;
    const hasTimeIn = (form.intime ?? '').trim().length > 0;

    const missing: string[] = [];
    if (!form.type) missing.push('Type');
    if (required.driver && !(form.driver ?? '').trim()) missing.push('Driver');
    if (required.eventDate && !(form.eventDate ?? '').trim()) missing.push('Date');
    if (required.origin && !hasOrig) {
      // Match the label the user actually sees: drayage shows "Origin",
      // waiting shows "Location", default shows "Location" until a
      // destination is filled (then it splits into Origin + Destination).
      const usesOriginLabel = variant === 'drayage' || (variant === 'default' && hasDest);
      missing.push(usesOriginLabel ? 'Origin' : 'Location');
    }
    if (required.destination && !hasDest) missing.push('Destination');
    if (required.rate && !hasRate) missing.push('Pay');
    if (required.timeIn && !hasTimeIn) missing.push('Time In');
    if (required.fuelType && !hasFuelType) missing.push('Fuel Type');

    if (missing.length > 0) {
      setError(`${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`);
      return;
    }

    // #33649 — a Bobtail with no rate on file cannot be approved. The dropdown
    // already disables PAY, but an existing row can arrive already sitting at
    // PAY (or lose its rate when the date/lane is edited), so this catches the
    // value rather than the click. The backend enforces the same rule.
    if (payBlockedByMissingRate && (form.payStatus ?? '').trim().toUpperCase() === 'PAY') {
      setError(missingRateMessage);
      return;
    }

    // Soft pre-save warnings (V1's sequential window.confirm gates: ±30-day
    // date, duplicate type+rate, rate over $5000) now surface in a single
    // confirmation modal. If any apply, open it and wait for the user to
    // confirm before continuing to the save.
    const warnings = computeSaveWarnings(form, existingAdditions, recno, Date.now());
    if (warnings.length > 0) {
      setSaveWarnings(warnings);
      return;
    }

    await proceedWithSave();
  };

  /** Continue a save past the soft warnings: the over-limit gate (its own
   *  confirm overlay) then the actual persist. Called directly by handleSave
   *  when there are no warnings, or by the warnings modal's Continue button. */
  const proceedWithSave = async () => {
    // Over the per-type limit: no longer a hard block. Open the confirm overlay
    // so the user can proceed with an optional reason; the backend logs the
    // override to the Pro History tab.
    if (limitExceeded) {
      setOverrideReason('');
      setLimitConfirm({ rate: rateNum, limit: limitAmount });
      return;
    }
    await doSave(false, '');
  };

  /** Confirm the soft pre-save warnings: close the overlay and continue. */
  const confirmSaveWarnings = async () => {
    setSaveWarnings(null);
    await proceedWithSave();
  };

  /** Toast the outcome of the customer-invoice line-item auto-add that the
   *  backend performs for a new Bobtail / Waiting Time addition. No-op for
   *  every other type (and for updates), where the backend sends no outcome. */
  const notifyInvoiceLineItem = (created: DriverPayAddition) => {
    const toast = invoiceLineItemToast(created.invoiceLineItemOutcome, created.invoiceLineItemLabel);
    if (toast) showToast(toast);
  };

  /** Build the patch and persist. When `override` is set (the user confirmed a
   *  rate over the type's limit) the flag + optional reason ride along so the
   *  backend allows the save and records it in history. */
  const doSave = async (override: boolean, reason: string) => {
    setSaving(true);
    setError(null);
    try {
      const patch: DriverPayAdditionPatch = {
        type: form.type,
        driver: form.driver,
        eventDate: form.eventDate,
        origCode: form.origCode ?? 0,
        destCode: form.destCode ?? 0,
        rate: form.rate ?? 0,
        fuelType: form.fuelType ?? '',
        description: form.description ?? '',
        payStatus: form.payStatus ?? '',
        donotpayReason: form.donotpayReason ?? '',
        deleted: form.deleted ?? 'N',
        intime: form.intime ?? '',
        outtime: form.outtime ?? '',
        hours: form.hours ?? 0,
      };
      // Bug 32837 — gwicommission is no longer sent from the modal; the
      // backend derives it authoritatively from the type's catalogue flag.
      if (override) {
        patch.limitOverride = true;
        if (reason.trim()) patch.limitOverrideReason = reason.trim();
      }
      if (recno == null) {
        const created = await driverPayAdditionsService.createAddition(pronumber, patch);
        notifyInvoiceLineItem(created);
      } else {
        await driverPayAdditionsService.updateAddition(pronumber, recno, patch);
      }
      await onSaved();
      onClose();
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || (err instanceof Error ? err.message : 'Failed to save addition'));
    } finally {
      setSaving(false);
    }
  };

  /** Confirm the over-limit override: close the overlay and save with the flag. */
  const confirmLimitOverride = async () => {
    setLimitConfirm(null);
    await doSave(true, overrideReason);
  };

  /** Footer Delete: open the confirm overlay (was a window.confirm gate). */
  const handleDelete = () => {
    if (recno == null || !canWrite) return;
    setDeleteConfirm(true);
  };

  /** Confirm delete: close the overlay and remove the addition. */
  const confirmDelete = async () => {
    if (recno == null) return;
    setDeleteConfirm(false);
    setSaving(true);
    setError(null);
    try {
      await driverPayAdditionsService.deleteAddition(pronumber, recno);
      await onSaved();
      onClose();
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || (err instanceof Error ? err.message : 'Failed to delete addition'));
    } finally {
      setSaving(false);
    }
  };

  // Alt+S / Alt+C, the same chords as the Pro footer behind us. Claiming the
  // layer is what keeps Alt+S from reaching the drawer's Save Changes. Save
  // mirrors its button exactly: same permission gate, hidden once the addition
  // is paid (`locked`), and unbound while either of this modal's own overlays —
  // the soft-warning list and the over-limit confirm — is up. The layer keeps
  // swallowing the chord in those states, so it never leaks downward.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      S:
        isOpen && !saving && !loading && !locked && !limitConfirm && !saveWarnings && saveAccess >= 2
          ? handleSave
          : null,
      C: isOpen && !saving && !limitConfirm && !saveWarnings ? onClose : null,
    },
    rootRef,
    { layer: isOpen },
  );

  // Header-drag for the main modal and each of its confirm overlays.
  const mainDrag = useModalDrag(isOpen);
  const limitDrag = useModalDrag(!!limitConfirm);
  const warningsDrag = useModalDrag(!!saveWarnings);
  const deleteDrag = useModalDrag(deleteConfirm);

  if (!isOpen) return null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={recno == null ? 'New Driver Pay Addition' : 'Driver Pay Addition'}
      className="fixed inset-0 z-[100] overflow-y-auto"
      data-testid="driver-pay-addition-modal"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 sm:p-0">
        <div className="fixed inset-0 z-[100] bg-gray-500 dark:bg-gray-900 opacity-75" onClick={onClose} />
        <div
          ref={mainDrag.panelRef}
          style={mainDrag.panelStyle}
          className="relative z-[101] inline-block bg-panel rounded-lg shadow-xl sm:my-8 max-w-[780px] w-full text-left"
        >
          {/* Header */}
          <ModalHeader
            title={recno == null ? 'New Driver Pay Addition' : `Addition: ${form.type || ''}`}
            onClose={onClose}
            dragHandleProps={mainDrag.dragHandleProps}
          />

          {/* Body */}
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="mb-2 px-2 py-1 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-center text-[0.75rem] text-slate-500 dark:text-gray-400 py-4">Loading…</div>
            )}

            {!loading && refData && (
              <div className="space-y-3">
                {locked && (
                  <div
                    data-testid="dpa-paid-lock-banner"
                    className="flex items-start gap-1.5 px-2 py-1.5 text-[0.75rem] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded"
                  >
                    <LockClosedIcon className="w-3.5 h-3.5 mt-px flex-shrink-0" aria-hidden="true" />
                    <span>
                      This addition is on an invoiced payroll
                      {form.payrollDate ? ` (payroll date ${form.payrollDate})` : ''} and is
                      locked — it can’t be edited or deleted.
                    </span>
                  </div>
                )}
                {/* When paid — or the caller only holds View (1) on the driver-pay
                    component — the entire form is view-only: a disabled fieldset
                    inert-ifies every native control and pointer-events-none stops
                    the custom typeaheads/date pickers from opening. */}
                <fieldset
                  disabled={locked || !canWrite}
                  className={`space-y-3 min-w-0 border-0 p-0 m-0${locked || !canWrite ? ' pointer-events-none' : ''}`}
                >
                {/* Type */}
                <div>
                  <Label htmlFor="dpa-type" required={required.type}>Type</Label>
                  <select
                    id="dpa-type"
                    className={inputClass}
                    value={form.type ?? ''}
                    onChange={(e) => {
                      // Switching the type from the dropdown clears the
                      // rest of the form so stale driver/date/location
                      // values from the previous type don't leak in.
                      rateTouchedRef.current = false;
                      setForm(freshForm(e.target.value));
                    }}
                    disabled={recno != null}
                  >
                    <option value="">— Select —</option>
                    {typeOptions.legacy.map((n) => (
                      <option key={n} value={n} disabled>{n} (inactive)</option>
                    ))}
                    {refData.additionTypes.map((t) => (
                      <option key={t.name} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                  {/* V1 %typedescription: help text for the selected type,
                      shown directly under the dropdown (lib_editrecord.pl:2003). */}
                  {selectedType?.description && (
                    <p
                      data-testid="dpa-type-description"
                      className="mt-1 text-[0.75rem] text-slate-500 dark:text-gray-400"
                    >
                      {selectedType.description}
                    </p>
                  )}
                </div>

                {/* Variant-specific top fields */}
                {variant === 'drayage' && (
                  <>
                    <div className="grid grid-cols-[1fr_140px] gap-2">
                      <DriverField form={form} refData={refData} onField={onField} required={required.driver} />
                      <DateField form={form} onField={onField} required={required.eventDate} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <OriginField
                        form={form}
                        onField={onField}
                        required={required.origin}
                      />
                      <DestField
                        form={form}
                        onField={onField}
                        required={required.destination}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 [&>*]:flex-1 [&>*]:min-w-[110px]">
                      <RateField
                        form={form}
                        onField={onField}
                        readOnly={form.type === 'BOBTAIL'}
                        loading={rateLoading}
                        required={required.rate}
                      />
                      {fuelField}
                      {commissionApplies && <ReadonlyMoney label="Comm" value={formatMoney(-(commission ?? 0))} negative />}
                      {fuelAmountField}
                      {(commissionApplies || showsFuel) && <ReadonlyMoney label="Net" value={netDisplay} />}
                    </div>
                    {/* #33649 — the rate engine found nothing for this lane. */}
                    {missingBobtailRate && !rateLoading && (
                      <div
                        role="status"
                        data-testid="missing-bobtail-rate"
                        className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      >
                        {missingRateMessage}
                      </div>
                    )}
                  </>
                )}

                {variant === 'waiting' && (
                  <>
                    <div className="grid grid-cols-[1fr_140px] gap-2">
                      <DriverField form={form} refData={refData} onField={onField} required={required.driver} />
                      <DateField form={form} onField={onField} required={required.eventDate} />
                    </div>
                    {/* Location / In / Out on one line (Bug 32837). Location
                        fills the row; In/Out are compact time fields. */}
                    <div className="grid grid-cols-[1fr_100px_100px] gap-2">
                      <div>
                        <Label required={required.origin}>Location</Label>
                        <select
                          className={inputClass}
                          value={form.origCode ?? 0}
                          onChange={(e) => {
                            const code = parseInt(e.target.value, 10);
                            const loc = refData.legLocations.find((l) => l.code === code);
                            onField('origCode', code);
                            if (loc) onField('origName', loc.name);
                          }}
                        >
                          <option value={0}>— Select location —</option>
                          {refData.legLocations.map((l) => (
                            <option key={l.code} value={l.code}>
                              {l.name}{l.addr ? ` [${l.addr}]` : ''} · #{l.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label required={required.timeIn}>In</Label>
                        <DateTimePicker
                          mode="time"
                          inputVariant="compact"
                          selectionType="single"
                          openDirection="down"
                          autoFlip
                          usePortal
                          labelType="none"
                          placeholder="Time In"
                          value={parseHHMM(form.intime ?? '')}
                          onChange={(v) => onField('intime', formatHHMM(v as Date | null))}
                        />
                      </div>
                      <div>
                        <Label>Out</Label>
                        <DateTimePicker
                          mode="time"
                          inputVariant="compact"
                          selectionType="single"
                          openDirection="down"
                          autoFlip
                          usePortal
                          labelType="none"
                          placeholder="Time Out"
                          value={parseHHMM(form.outtime ?? '')}
                          onChange={(v) => onField('outtime', formatHHMM(v as Date | null))}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 [&>*]:flex-1 [&>*]:min-w-[100px]">
                      <div>
                        <Label>Hours</Label>
                        <input
                          type="number"
                          step="0.01"
                          className={readonlyClass}
                          value={form.hours ?? 0}
                          readOnly
                          aria-label="Hours (auto-computed)"
                        />
                      </div>
                      <div>
                        <Label>Per Rate</Label>
                        <input
                          type="number"
                          step="0.01"
                          className={readonlyClass}
                          value={perRate}
                          readOnly
                          aria-label="Per-hour rate (from driverpay_rates_other)"
                        />
                      </div>
                      {/* V1 lib_editrecord.pl:3880 renders add_rate as
                          readonly for WAITING TIME — the amount is computed
                          as hours x perrate by the calctime effect above. */}
                      <RateField form={form} onField={onField} label="Amount" readOnly loading={rateLoading} />
                      {commissionApplies && <ReadonlyMoney label="Comm" value={formatMoney(-(commission ?? 0))} negative />}
                      {fuelAmountField}
                      {(commissionApplies || showsFuel) && <ReadonlyMoney label="Net" value={netDisplay} />}
                    </div>
                  </>
                )}

                {variant === 'default' && (() => {
                  // V1 parity (lib_editrecord.pl:3922-3928): the default
                  // variant shows a single "Location" field unless the
                  // existing row already has a destination — in which case
                  // it splits into Origin + Destination. New rows always
                  // start as single Location.
                  const showDest = (form.destCode ?? 0) !== 0;
                  return (
                    <>
                      <div className="grid grid-cols-[1fr_140px] gap-2">
                        <DriverField form={form} refData={refData} onField={onField} required={required.driver} />
                        <DateField form={form} onField={onField} required={required.eventDate} />
                      </div>
                      <div className={`grid gap-2 ${showDest ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        <OriginField
                          form={form}
                          onField={onField}
                          label={showDest ? 'Origin' : 'Location'}
                          required={required.origin}
                        />
                        {showDest && (
                          <DestField
                            form={form}
                            onField={onField}
                            required={required.destination}
                          />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 [&>*]:flex-1 [&>*]:min-w-[110px]">
                        <RateField
                          form={form}
                          onField={onField}
                          readOnly={AUTO_RATE_LOCK_TYPES.has(form.type ?? '')}
                          loading={rateLoading}
                          required={required.rate}
                        />
                        {fuelField}
                        {commissionApplies && <ReadonlyMoney label="Comm" value={formatMoney(-(commission ?? 0))} negative />}
                        {fuelAmountField}
                        {(commissionApplies || showsFuel) && <ReadonlyMoney label="Net" value={netDisplay} />}
                      </div>
                    </>
                  );
                })()}

                {/* Over-limit is a soft warning now: Save stays enabled and
                    opens a confirmation prompt (logged to the Pro History tab)
                    rather than hard-blocking the rate. */}
                {limitExceeded && (
                  <div
                    data-testid="dpa-limit-warning"
                    className="px-2 py-1 text-[0.75rem] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded"
                  >
                    Rate exceeds the {form.type} limit of {limitAmount.toFixed(2)}. You'll be asked to confirm on Save.
                  </div>
                )}

                {/* Description (uppercase-on-blur matches V1 line 3965) */}
                <div>
                  <Label>Description</Label>
                  <textarea
                    rows={3}
                    className={inputClass.replace('h-7', 'h-auto py-1')}
                    value={form.description ?? ''}
                    onChange={(e) => onField('description', e.target.value)}
                    onBlur={(e) => onField('description', e.target.value.toUpperCase())}
                  />
                </div>

                {/* Shared bottom row: Pay Status + Reason (Delete lives in the footer) */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Pay Status</Label>
                    <select
                      className={inputClass}
                      aria-label="Pay Status"
                      value={form.payStatus ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        onField('payStatus', v);
                        // Reason only applies to DO NOT PAY / DEFER; clear it
                        // when the user returns to PAY / blank (Bug 32837).
                        if (['', 'PAY'].includes(v.trim())) onField('donotpayReason', '');
                      }}
                    >
                      {payStatusOptions.map((s) => (
                        <option
                          key={s || '_empty'}
                          value={s}
                          // #33649 — PAY is unreachable while the Bobtail rate is
                          // missing. DEFER stays open on purpose: holding the line
                          // until someone loads the rate is the right move.
                          disabled={
                            (s !== '' && !refData.payStatuses.includes(s))
                            || (missingBobtailRate && s.trim().toUpperCase() === 'PAY')
                          }
                        >
                          {s || '—'}
                        </option>
                      ))}
                    </select>
                  </div>
                  {reasonApplies && (
                    <div>
                      <Label>Reason</Label>
                      <select
                        className={inputClass}
                        aria-label="Reason"
                        value={form.donotpayReason ?? ''}
                        onChange={(e) => onField('donotpayReason', e.target.value)}
                      >
                        {(refData.donotpayReasons.includes(form.donotpayReason ?? '')
                          ? refData.donotpayReasons
                          : [...refData.donotpayReasons, form.donotpayReason ?? '']
                        ).map((r) => (
                          <option
                            key={r || '_empty'}
                            value={r}
                            disabled={r !== '' && !refData.donotpayReasons.includes(r)}
                          >
                            {r || '—'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Shared bottom row 2: Approved by + Approved at + payroll date (read-only) */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Approved By</Label>
                    <input className={readonlyClass} value={form.approvedBy ?? ''} readOnly />
                  </div>
                  <div>
                    <Label>Approved At</Label>
                    <input className={readonlyClass} value={form.approvedDatetime ?? ''} readOnly />
                  </div>
                  <div>
                    <Label>Payroll Date</Label>
                    <input className={readonlyClass} value={form.payrollDate ?? ''} readOnly />
                  </div>
                </div>
                </fieldset>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
            {/* Paid rows and View-level callers are view-only: no Delete / Save,
                just a Close button. */}
            {recno != null && !locked && canWrite && (
              <PermissionsContainer
                componentId={DRIVER_PAY_PERMISSION_ID}
                componentName={DRIVER_PAY_PERMISSION_NAME}
                showCog={false}
              >
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-3 py-1.5 text-[0.75rem] font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                >
                  Delete
                </button>
              </PermissionsContainer>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-[0.75rem] font-medium text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {locked || !canWrite ? 'Close' : 'Cancel'}
            </button>
            {!locked && canWrite && (
              <PermissionsContainer
                componentId={DRIVER_PAY_PERMISSION_ID}
                componentName={DRIVER_PAY_PERMISSION_NAME}
                showCog={false}
              >
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </PermissionsContainer>
            )}
          </div>
        </div>
      </div>

      {/* Over-limit confirmation — soft override with an optional reason. On
          confirm the save carries limitOverride so the backend logs it to the
          Pro History tab (keyed to this PRO). */}
      {limitConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4" data-testid="dpa-limit-confirm">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={() => setLimitConfirm(null)} />
          <div
            ref={limitDrag.panelRef}
            style={limitDrag.panelStyle}
            className="relative z-[111] bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Confirm rate over limit"
              onClose={() => setLimitConfirm(null)}
              dragHandleProps={limitDrag.dragHandleProps}
            />
            <div className="px-4 py-3 space-y-3">
              <p className="text-[0.8125rem] text-slate-700 dark:text-gray-200">
                Rate <span className="font-semibold">{limitConfirm.rate.toFixed(2)}</span> exceeds the{' '}
                {form.type} limit of <span className="font-semibold">{limitConfirm.limit.toFixed(2)}</span>. Add anyway?
              </p>
              <div>
                <Label htmlFor="dpa-override-reason">Override reason (optional)</Label>
                <textarea
                  id="dpa-override-reason"
                  aria-label="Override reason (optional)"
                  rows={2}
                  className={inputClass.replace('h-7', 'h-auto py-1')}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Why is this over-limit pay approved? (logged to history)"
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setLimitConfirm(null)}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-medium text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLimitOverride}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-amber-600 border border-amber-600 rounded hover:bg-amber-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Add anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Soft pre-save warnings — the ±30-day date / duplicate-rate / over-$5000
          gates V1 raised as sequential window.confirm() dialogs, now one modal.
          Confirming continues to the over-limit gate (if any) and then saves. */}
      {saveWarnings && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4" data-testid="dpa-save-warnings">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={() => setSaveWarnings(null)} />
          <div
            ref={warningsDrag.panelRef}
            style={warningsDrag.panelStyle}
            className="relative z-[111] bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Please confirm"
              onClose={() => setSaveWarnings(null)}
              dragHandleProps={warningsDrag.dragHandleProps}
            />
            <div className="px-4 py-3 space-y-2">
              {saveWarnings.length === 1 ? (
                <p className="text-[0.8125rem] text-slate-700 dark:text-gray-200">{saveWarnings[0]} Continue?</p>
              ) : (
                <>
                  <p className="text-[0.8125rem] text-slate-700 dark:text-gray-200">Please review before continuing:</p>
                  <ul className="list-disc pl-5 space-y-1 text-[0.8125rem] text-slate-700 dark:text-gray-200">
                    {saveWarnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveWarnings(null)}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-medium text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSaveWarnings}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — was a window.confirm on the footer Delete. */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4" data-testid="dpa-delete-confirm">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={() => setDeleteConfirm(false)} />
          <div
            ref={deleteDrag.panelRef}
            style={deleteDrag.panelStyle}
            className="relative z-[111] bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Delete addition"
              onClose={() => setDeleteConfirm(false)}
              dragHandleProps={deleteDrag.dragHandleProps}
            />
            <div className="px-4 py-3">
              <p className="text-[0.8125rem] text-slate-700 dark:text-gray-200">
                Delete this driver pay addition? This can’t be undone.
              </p>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-medium text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={saving}
                className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-red-600 border border-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Field components — extracted so each variant block stays short.
// ---------------------------------------------------------------------------

interface FieldProps {
  form: FormState;
  refData: DriverPayAdditionReferenceData;
  onField: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}

const DriverField: React.FC<FieldProps & { required?: boolean }> = ({
  form,
  refData,
  onField,
  required = false,
}) => {
  const options = useMemo<TypeAheadOption[]>(
    () =>
      refData.drivers.map((d) => {
        const trailing = d.status && d.status !== 'IN SERVICE' ? ` (${d.status})` : '';
        return {
          value: d.unitnumber,
          label: `${d.name ? `${d.unitnumber} - ${d.name}` : d.unitnumber}${d.isMule ? " (Mule)" : ""}${trailing}`,
        };
      }),
    [refData.drivers],
  );
  return (
    <div>
      <Label required={required}>Driver</Label>
      <TypeAhead
        options={options}
        value={form.driver ?? ''}
        onChange={(v) => onField('driver', typeof v === 'string' ? v : '')}
        placeholder="Search driver…"
        compact
        showSearch
      />
    </div>
  );
};

const DateField: React.FC<{ form: FormState; onField: FieldProps['onField']; required?: boolean }> = ({
  form,
  onField,
  required = false,
}) => (
  <div>
    <Label required={required}>Date</Label>
    <DateTimePicker
      mode="date"
      inputVariant="compact"
      selectionType="single"
      autoApply
      openDirection="down"
      autoFlip
      usePortal
      labelType="none"
      placeholder="Date"
      value={parseMdYDate(form.eventDate ?? '')}
      onChange={(v) => onField('eventDate', formatMdYDate(v as Date | null))}
    />
  </div>
);

/** The address line shown under a picked location — same two-part shape the
 *  picker's own rows use ("1240 U.S. 77 · Hillsboro TX 76645"). */
function locationAddressLine(loc: LocationLite): string {
  const cityStateZip = [loc.lcity, loc.lstate, loc.lzip]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return [(loc.ladd ?? '').trim(), cityStateZip].filter(Boolean).join(' · ');
}

interface LocationFieldProps {
  label: 'Origin' | 'Destination' | 'Location';
  code: number;
  name: string;
  onPick: (loc: LocationLite | null) => void;
  required?: boolean;
}

/**
 * Origin / Destination / Location picker — the shared LocationTypeAhead
 * (`GET /admin/locations?q=`), the same lookup the legs grid and every
 * tablemaint drawer use.
 *
 * It replaced a bespoke prefix-matching lookup of this modal's own, which
 * couldn't find a location whose stored name carried a leading space and never
 * matched a location # the way the other pickers do. Searching by name, alias,
 * city, address or recno now behaves identically everywhere.
 */
const LocationField: React.FC<LocationFieldProps> = ({
  label, code, name, onPick, required = false,
}) => {
  const [address, setAddress] = useState('');

  // A row loaded for editing arrives as code + name only. The shared search
  // resolves an all-digit term against recno, so the location's own # fetches
  // its address in one call. A deleted location just leaves the line blank.
  useEffect(() => {
    if (!code) { setAddress(''); return; }
    let alive = true;
    searchLocations(String(code), { limit: 1 })
      .then((results) => {
        const match = results.find((r) => r.recno === code);
        if (alive && match) setAddress(locationAddressLine(match));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [code]);

  return (
    <div className="min-w-0">
      <Label required={required}>{label}</Label>
      <LocationTypeAhead
        value={code ? String(code) : ''}
        valueLabel={name}
        onPick={(loc) => {
          setAddress(loc ? locationAddressLine(loc) : '');
          onPick(loc);
        }}
        placeholder={`Search ${label.toLowerCase()}…`}
        ariaLabel={label}
        compact
        withRecnoSuffix
      />
      <div className="mt-0.5 text-[0.65625rem] text-slate-500 dark:text-gray-400 h-4 truncate">
        {code ? (
          <span className="mr-1.5 font-semibold text-green-700 dark:text-green-400">#{code}</span>
        ) : ''}
        {address}
      </div>
    </div>
  );
};

const OriginField: React.FC<Pick<FieldProps, 'form' | 'onField'> & {
  label?: 'Origin' | 'Location';
  required?: boolean;
}> = ({ form, onField, label = 'Origin', required = false }) => (
  <LocationField
    label={label}
    code={form.origCode ?? 0}
    name={form.origName ?? ''}
    required={required}
    onPick={(loc) => {
      onField('origCode', loc ? loc.recno : 0);
      // lname, not the alias-preferred display name: origName is display-only
      // state that the server re-derives from locations.lname on reload, so an
      // alias here would flip to the name the moment the row is reopened.
      onField('origName', loc ? loc.lname : '');
    }}
  />
);

const DestField: React.FC<Pick<FieldProps, 'form' | 'onField'> & { required?: boolean }> = ({
  form, onField, required = false,
}) => (
  <LocationField
    label="Destination"
    code={form.destCode ?? 0}
    name={form.destName ?? ''}
    required={required}
    onPick={(loc) => {
      onField('destCode', loc ? loc.recno : 0);
      onField('destName', loc ? loc.lname : '');
    }}
  />
);

const RateField: React.FC<{
  form: FormState;
  onField: FieldProps['onField'];
  label?: string;
  readOnly?: boolean;
  required?: boolean;
  /** When a read-only rate is auto-pulling, show a "Pulling Rate…" indicator. */
  loading?: boolean;
}> = ({ form, onField, label = 'Rate', readOnly = false, required = false, loading = false }) => {
  const numeric = form.rate ?? 0;
  // Hold the raw text the user is typing in local state so partial entries
  // like "-", "3." or "-5.0" survive re-renders. A controlled type="number"
  // input can't hold those — it reports value="" for an incomplete number and
  // the parsed value snaps back to 0, which is why starting with "-" failed.
  // We push a parsed number up to form state and only resync the text when
  // form.rate changes from the outside (auto-rate lookup, waiting-time calc,
  // type switch); lastEmitted keeps our own keystrokes from being clobbered.
  const [raw, setRaw] = useState<string>(() => String(numeric));
  const lastEmitted = useRef<number>(numeric);

  useEffect(() => {
    if (numeric !== lastEmitted.current) {
      lastEmitted.current = numeric;
      setRaw(String(numeric));
    }
  }, [numeric]);

  const showPulling = readOnly && loading;
  return (
    <div>
      <Label required={required && !readOnly}>{label}</Label>
      <input
        type="text"
        inputMode="decimal"
        className={
          (readOnly ? readonlyClass : inputClass) +
          (showPulling ? ' italic text-slate-400 dark:text-gray-500 animate-pulse' : '')
        }
        value={readOnly ? (showPulling ? 'Pulling Rate…' : String(numeric)) : raw}
        // Select the whole value on focus so the seeded "0" is replaced by the
        // first keystroke instead of leaving a leading zero ("05"). Same idiom
        // as ApiCustomers' numeric fields.
        onFocus={readOnly ? undefined : (e) => e.currentTarget.select()}
        onChange={(e) => {
          const v = e.target.value;
          // Allow empty, a lone "-", and partial decimals mid-type.
          if (!/^-?\d*\.?\d*$/.test(v)) return;
          setRaw(v);
          const n = parseFloat(v);
          const next = Number.isFinite(n) ? n : 0;
          lastEmitted.current = next;
          onField('rate', next);
        }}
        readOnly={readOnly}
        aria-label={readOnly ? `${label} (auto-computed)` : label}
      />
    </div>
  );
};

export default DriverPayAdditionModal;
